import React, { useContext, useEffect, useState } from 'react'
import { ArtData } from '@/database/art'
import { Context, Tokenomics, UserShare } from '@/components/Context'
import { listing } from '@/utils/mapping'
import { getUserShare } from '@/utils/calculations'
import { shortenAddress } from '@/utils/shortenAddress'
import { copyToClipboard, uni1d800 } from '@/utils/extras'
import Image from 'next/image'
import '@/css/tokenomics.css'

const calcTotal = (userShare: UserShare, tokenomics: Tokenomics) => {
    const total = (
        (userShare.claims * tokenomics.claims) / 100 +
        (userShare.creator * tokenomics.creator) / 100 +
        (userShare.owner * tokenomics.owner) / 100
    ).toFixed(2)
    return total !== 'NaN' ? total : '-'
}

const TokenomicsOverview = ({ artData }: { artData: ArtData | undefined }): React.JSX.Element => {
    const { getTokenomics, currentAccount, artDataList } = useContext(Context)

    const [tokenomics, setTokenomics] = useState({} as Tokenomics)
    const [currentUserShare, setCurrentUserShare] = useState({} as UserShare)
    const [showTooltip, setShowTooltip] = useState(false)

    useEffect(() => {
        if (artData) {
            setTokenomics(getTokenomics(artData))
            if (currentAccount) {
                setCurrentUserShare(getUserShare(artData, currentAccount, artDataList))
            }
        }
    }, [artData, currentAccount])

    return (
        <div className="tokenomics-container">
            {artData && (
                <div>
                    <div className="table-container">
                        <table className="tokenomics-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="right-align">Status:</td>
                                    <td className="left-align">{listing(artData)}</td>
                                </tr>
                                <tr>
                                    <td className="right-align">
                                        <span className={uni1d800.className + ' gradient-text'}>
                                            {'\u200C\uD836\uDE17'}CRAZY{'\u200C\uD836\uDE17'} Transfer Fee:
                                        </span>
                                    </td>
                                    <td className="left-align">
                                        <span className={uni1d800.className + ' white'}>
                                            {'\u200C\uD836\uDE17'}
                                            {artData.art.transferFee}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <span className="white">
                            <div>Upon settling the transfer fee OR finalizing an auction OR bid,</div>
                            <span>the earnings will be divided as follows:</span>
                        </span>

                        <table className="tokenomics-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="right-align gray">DAO/Contract Fee:</td>
                                    <td className="left-align white">{tokenomics.fee}%</td>
                                </tr>
                                <tr>
                                    <td className="right-align">Current Owner:</td>
                                    <td className="left-align">{tokenomics.owner}%</td>
                                </tr>
                                <tr>
                                    <td className="right-align">Creator Royalty:</td>
                                    <td className="left-align">{tokenomics.creator}%</td>
                                </tr>
                                <tr>
                                    <td className="right-align">Burn:</td>
                                    <td className="left-align">{tokenomics.burn}%</td>
                                </tr>
                                <tr>
                                    <td className="right-align">Buy & Burn Token:</td>
                                    <td className="left-align">
                                        {tokenomics.burnToken ? (
                                            <>
                                                <Image
                                                    src="/hexagon-copy.svg"
                                                    alt="copy"
                                                    width={15}
                                                    height={15}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() =>
                                                        copyToClipboard(tokenomics.burnToken, setShowTooltip)
                                                    }
                                                />
                                                &nbsp;{!showTooltip && shortenAddress(tokenomics.burnToken)}
                                                {showTooltip && <span> Copied! </span>}
                                            </>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="right-align">Airdrop:</td>
                                    <td className="left-align">{tokenomics.airdrop}%</td>
                                </tr>
                                <tr>
                                    <td className="right-align gray">Distributed Votes:</td>
                                    <td className="left-align white">{tokenomics.votes}%</td>
                                </tr>
                                <tr>
                                    <td className="right-align gray">Distributed Claims:</td>
                                    <td className="left-align white">{tokenomics.claims}%</td>
                                </tr>
                                <tr>
                                    <td className="right-align gray">Treasury:</td>
                                    <td className="left-align white">{tokenomics.treasury}%</td>
                                </tr>
                            </tbody>
                        </table>

                        <p className="white">Your royalties share:</p>
                        <table className="tokenomics-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="right-align gray">As Creator:</td>
                                    <td className="left-align white">
                                        {currentUserShare.creator
                                            ? ((currentUserShare.creator * tokenomics.creator) / 100).toFixed(2)
                                            : '-'}
                                        %
                                    </td>
                                </tr>
                                <tr>
                                    <td className="right-align">As Owner:</td>
                                    <td className="left-align">
                                        {currentUserShare.owner
                                            ? ((currentUserShare.owner * tokenomics.owner) / 100).toFixed(2)
                                            : '-'}
                                        %
                                    </td>
                                </tr>
                                <tr>
                                    <td className="right-align">Your Claims:</td>
                                    <td className="left-align">
                                        {currentUserShare.claims
                                            ? ((currentUserShare.claims * tokenomics.claims) / 100).toFixed(2)
                                            : '-'}
                                        %
                                    </td>
                                </tr>
                                <tr>
                                    <td className="right-align">Total:</td>
                                    <td className="left-align">{calcTotal(currentUserShare, tokenomics)}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default TokenomicsOverview
